import {
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  Layout,
  Row,
  Select,
  Skeleton,
  Switch,
  Tree,
  TreeSelect
} from "antd";
import { get, toNumber } from "lodash";
import React, { useEffect, useMemo, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModalDeleteConfirmTreePhongBan from "src/components/common/ModalDeleteConfirmTreePhongBan";
import { messages } from "src/constants/messages";
import Helper from "src/helpers/index";
import { MessageStatusError } from "src/util/MessageStatus";
import { apiCaller, cancelListToken } from "src/util/apiCaller";

const { Search } = Input;
const listCap = [
  { cap: 1, name: "Sở Ban Ngành" },
  { cap: 2, name: "Ngành dọc" },
  { cap: 3, name: "Huyện" },
  { cap: 4, name: "Xã" }
];

const initialState = {
  tenDonVi: "",
  isDonVi: false,
  maDinhDanh: "",
  thuTu: "",
  parentId: 0
};

const FormItem = Form.Item;

interface PhongBan {
  id: number;
  tenDonVi: string;
  cap: number;
  isDonVi: boolean;
  maDinhDanh: "";
  parentId: number;
  thuTu: number;
  children: PhongBan[];
}

const PhongBan = ({ status }: any) => {
  const { isActive } = status;
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [id, setId] = useState(undefined);
  const [parentId, setParentId] = useState(0);
  const [cap, setCap] = useState(1);
  const [isLoading, setLoading] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingButtonDelete, setLoadingButtonDelete] = useState(false);
  const [treeDataPB, setTreeDataPB] = useState<PhongBan | undefined>();
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const [expandedKeys, setExpandedKeys] = useState([1]);
  const { tenDonVi, isDonVi, thuTu, maDinhDanh } = initialState;
  const [form] = Form.useForm();
  const { validateFields, setFieldsValue } = form;
  const [itemEdit, setItemEdit] = useState<any | undefined>([]);
  const [type, setType] = useState("");

  useEffect(() => {
    if (isActive) {
      getListData();
    } else {
      navigate("/error/401");
    }
  }, []);

  useEffect(() => {
    return () => {
      cancelListToken(["listPhongBan"]);
    };
  }, []);

  /**
   * getListData: gọi danh sách phòng ban
   * @memberof PhongBan
   */
  const getListData = async () => {
    setLoading(true);
    const configAPI = {
      url: `api/phongbans/gettreealluser`,
      method: "GET",
      value: null,
      apiType: "LIST",
      getName: "listPhongBan"
    };
    const response = await apiCaller(configAPI);
    try {
      if (
        response?.data &&
        (response.status === 200 || response.status === 201)
      ) {
        const data = get(response, "data", []);
        setTreeDataPB(data);
      } else {
        MessageStatusError(toNumber(response?.status));
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   *handleRedirect : Chuyển trang khi check type
   * @param {string} [type='']
   * @param {object} item
   * @memberof PhongBan
   */
  const handleRedirect = (type = "") => {
    let pathname = "/quan-tri/phong-ban";
    switch (type) {
      case "new":
        pathname += "/them-moi/";
        break;
      default:
        break;
    }
    navigate(pathname);
  };

  /**
   * generateList: Tạo 1 danh sách tạm để tìm kiếm với key và title
   * @param {array} data
   * @memberof PhongBan
   */
  const dataList: { key: any; title: any }[] = [];
  const generateList = (data: any) => {
    for (let i = 0; i < data?.length; i++) {
      const node = data[i];
      const { id, tenDonVi } = node;
      dataList.push({
        key: id,
        title: tenDonVi
      });
      if (node.children) {
        generateList(node.children);
      }
    }
  };
  generateList(treeDataPB);

  /**
   * getParentKey: Hàm lấy parent Key
   * @param {number} key
   * @param {array} tree
   * @memberof PhongBan
   */
  const getParentKey = (key: number, tree: any): number | undefined => {
    let parentKey: number | undefined;
    for (let i = 0; i < tree.length; i++) {
      const node = tree[i];
      if (node.children) {
        if (node.children.some((item: any) => item.id === key)) {
          parentKey = node.id;
        } else if (getParentKey(key, node.children)) {
          parentKey = getParentKey(key, node.children);
        }
      }
    }
    return parentKey;
  };

  /**
   * onChange: Tìm kiếm
   * @param {object} e
   * @memberof PhongBan
   */
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const newExpandedKeys = dataList
      .map((item) => {
        if (item.title.indexOf(value) > -1) {
          return getParentKey(item.key, treeDataPB);
        }
        return null;
      })
      .filter((item, i, self) => item && self.indexOf(item) === i);
    if (value) {
      const hasSearchTerm = (n: string | any[]) => n?.indexOf(value) !== -1;
      const filterData = (arr: any) =>
        arr?.filter(
          (n: { title: any; children: any }) =>
            hasSearchTerm(n.title) || filterData(n.children)?.length > 0
        );
      const filteredData = filterData(treeDataPB).map(
        (n: { children: any }) => {
          return {
            ...n,
            children: filterData(n.children)
          };
        }
      );
      setSearchValue(value);
      setAutoExpandParent(true);
      setTreeDataPB(filteredData);
      setExpandedKeys(newExpandedKeys as any);
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } else {
      setTreeDataPB(treeDataPB);
      setExpandedKeys([]);
      setSearchValue("");
      setAutoExpandParent(false);
    }
  };

  /**
   *generateTreeData: Vòng lặp hiển thị dữ liệu
   * @param {array} data
   * @memberof PhongBan
   */
  const generateTreeData = (data: any) => {
    if (!Array.isArray(data)) return [];
    const loop = (data: any) =>
      data?.map((item: any) => {
        const strTitle = item?.tenDonVi;
        const index = strTitle.indexOf(searchValue);
        const beforeStr = strTitle.substring(0, index);
        const afterStr = strTitle.slice(index + searchValue.length);
        const title =
          index > -1 ? (
            <span>
              {beforeStr}
              <span className="site-tree-search-value">
                {strTitle.substr(index, searchValue.length)}
              </span>
              {afterStr}
            </span>
          ) : (
            <span>{strTitle}</span>
          );

        if (item.children) {
          return {
            title,
            key: item.id,
            children: loop(item.children)
          };
        }
        return {
          title,
          key: item.id
        };
      });
    return loop(data);
  };

  /**
   * treeData: hiển thị tree
   * @memberof PhongBan
   */
  const treeData = useMemo(
    () => generateTreeData(Array.isArray(treeDataPB) ? treeDataPB : []),
    [searchValue, treeDataPB]
  );

  /**
   * alertErrorMessage : Báo lỗi
   * @param {string} message
   * @memberof PhongBan
   */
  const alertErrorMessage = (message: string | undefined) => {
    Helper.alertError(message);
  };

  /**
   * save: Lưu
   * @memberof PhongBan
   */
  const save = () => {
    if (id && id === parentId) {
      alertErrorMessage("Phòng ban con không được trùng phòng ban cha");
    } else {
      validateFields()
        .then((values) => {
          setLoadingButton(true);
          saveData(values.phongBan);
          forceUpdate();
          setTimeout(() => {
            getListData();
          }, 100);
        })
        .catch((error) => {
          error;
          setLoadingButton(false);
        });
    }
  };

  /**
   * saveData: Lưu dữ liệu
   * @param {object} phongban
   * @memberof PhongBan
   */
  const saveData = async (phongban: any) => {
    const newData = phongban;
    newData.loaiDonVi = cap;
    newData.level = cap;
    newData.isBanTiepDan = false;
    newData.id = id;
    newData.isSuDung = true;
    const configAPI = {
      url: `api/phongbans/${id}`,
      method: "PUT",
      data: newData,
      apiType: "EDIT",
      isShowMessage: true
    };
    const response = await apiCaller(configAPI);
    try {
      if (response?.status === 200 || response?.status === 201) {
        setFieldsValue({ phongBan: response.data });
        setLoadingButton(false);
      } else {
        MessageStatusError(toNumber(response?.status));
        setLoadingButton(false);
      }
    } catch (error) {
      setLoadingButton(false);
    }
  };

  /**
   * onExpand: Mở rộng cây phòng ban
   * @param {number} newExpandedKeys
   * @memberof PhongBan
   */
  const onExpand = (newExpandedKeys: any) => {
    setExpandedKeys(newExpandedKeys);
    setAutoExpandParent(false);
  };

  /**
   * onSelect: Lưu dữ liệu
   * @param {number} checkedKeys
   * @memberof PhongBan
   */
  const onSelect = async (checkedKeys: string | any[]) => {
    if (checkedKeys && checkedKeys.length > 0) {
      const id = checkedKeys[0];
      setId(id);
      setType("view");
      const configAPI = {
        url: `api/phongbans/${id}`,
        method: "GET",
        value: null,
        apiType: "DETAIL"
      };
      const response = await apiCaller(configAPI);
      try {
        if (response?.status === 200 || response?.status === 201) {
          setItemEdit(response.data);
          setFieldsValue({
            phongBan: response.data
          });
        } else {
          MessageStatusError(toNumber(response?.status));
        }
      } catch (error) {
        error;
        // Xử lý lỗi khi lưu dữ liệu không thành công
      }
    }
  };

  const editItemFunc = (item: any) =>{
    setType("edit");
    setFieldsValue({
      phongBan: item
    });
  }

  /**
   * deleteItemFunc: Xoá item theo item
   * @param {object} item
   * @memberof PhongBan
   */
  const deleteItemFunc = (item: any) => {
    validateFields()
      .then(() => {
        setLoadingButtonDelete(false);
        ModalDeleteConfirmTreePhongBan(
          deleteItemAction,
          item,
          getListData,
          setLoadingButtonDelete
        );
      })
      .catch((error) => {
        error;
        setLoadingButtonDelete(false);
      });
  };

  /**
   * deleteItemAction: Xóa item
   * @memberof PhongBan
   */
  const deleteItemAction = async () => {
    setLoadingButtonDelete(true);
    const config = {
      url: `api/phongbans/${id}`,
      apiType: "DELETE",
      value: null,
      method: "DELETE"
    };

    const response = await apiCaller(config);
    try {
      if (response?.status === 200 || response?.status === 201) {
        setLoading(true);
        setType("");
      } else {
        MessageStatusError(toNumber(response?.status));
        setLoading(false);
      }
    } catch (error) {
      error;
      setLoading(false);
      // Xử lý lỗi khi lưu dữ liệu không thành công
    }
  };

  /**
   * onChangeCap: Chọn loại đơn vị
   * @param {number} e
   *@memberof PhongBan
   */
  const onChangeCap = (e: React.SetStateAction<number>) => {
    setCap(e);
  };

  /**
   * treeDataSelect: Vòng lặp tìm treeData
   * @memberof PhongBan
   */
  const treeDataSelect = useMemo(() => {
    const loop = (data: any) =>
      data?.map((item: any) => {
        if (item.children) {
          return {
            title: item.tenDonVi,
            value: item.id,
            key: item.id,
            children: loop(item.children)
          };
        }
        return {
          title: item.tenDonVi,
          value: item.id,
          key: item.id
        };
      });
    return loop(treeDataPB);
  }, [treeDataPB]);

  const flattenData = (data: any[]) => {
    const result: any[] = [];
    if (!Array.isArray(data)) {
      return result;
    }
    const recurse = (items: any[]) => {
      for (const item of items) {
        // Push the current item into the result array
        result.push(item);
        // If there are children, call recurse on them
        if (item.children && item.children.length > 0) {
          recurse(item.children);
        }
      }
    };

    recurse(data);
    return result;
  };

  const renderDonViCha = (parentId: any) => {
    const flatArray = flattenData(treeDataPB as any);
    const itemPhongBan = flatArray.find((value: any) => value.id === parentId);
    const tenDonVi = itemPhongBan ? itemPhongBan.tenDonVi : "Không";
    return tenDonVi;
  };

  const renderCap = (cap: any) => {
    let capdv = "";
    switch (cap) {
      case 1:
        capdv = "Sở ban ngành";
        break;
      case 2:
        capdv = "Ngành dọc";
        break;
      case 3:
        capdv = "Huyện";
        break;
      case 4:
        capdv = "Xã";
        break;
      default:
    }
    return capdv;
  };

  return (
    <Layout>
      <Flex justify="space-between" align="center">
        <h3 style={{ color: "black", fontSize: 20 }}>Phòng ban</h3>
        <Button
          className="heading-button-addnew btn"
          type="primary"
          onClick={() => handleRedirect("new")}
          icon={<PlusOutlined />}
        >
          Thêm mới
        </Button>
      </Flex>
      <Card className="qti-card">
        <Search
          style={{ marginBottom: 8 }}
          placeholder="Tìm kiếm"
          onChange={onChange}
        />
        <Skeleton loading={isLoading}>
          <Row gutter={16} style={{ marginTop: 15 }}>
            <Col md={12} span={24}>
              {treeDataPB && (
                <Tree
                  defaultExpandedKeys={expandedKeys}
                  onExpand={onExpand}
                  expandedKeys={expandedKeys}
                  autoExpandParent={autoExpandParent}
                  treeData={treeData}
                  onSelect={onSelect}
                  switcherIcon={<DownOutlined />}
                />
              )}
            </Col>
            {type === "view" ? (
            <Col md={12} span={24}>
              <Row gutter={24}>
                <Col md={12} xs={24}>
                  <p>
                    <b>Tên đơn vị/Phòng ban: </b> {itemEdit?.tenDonVi}
                  </p>
                </Col>
                <Col md={12} xs={24}>
                  <p>
                    <b>Đơn vị cha: </b> {renderDonViCha(itemEdit?.parentId)}
                  </p>
                </Col>
                <Col md={12} xs={24}>
                  <p>
                    <b>Mã định danh: </b>
                    {itemEdit?.maDinhDanh}
                  </p>
                </Col>
                <Col md={12} xs={24}>
                  <p>
                    <b>Cấp: </b>
                    {renderCap(itemEdit?.cap)}
                  </p>
                </Col>
                <Col md={12} xs={24}>
                  <p>
                    <b>Đơn vị: </b>
                    {itemEdit?.isDonVi ? "Là đơn vị" : "Không"}
                  </p>
                </Col>
                <Col md={12} xs={24}>
                  <p>
                    <b>Thứ tự: </b>
                    {itemEdit?.thuTu}
                  </p>
                </Col>
              </Row>
              <hr />
              <div style={{ textAlign: "center", padding: 10 }}>
                <Button
                  style={{ marginLeft: "15px", background: "green" }}
                  className="heading-button-addnew btn"
                  onClick={() => editItemFunc(itemEdit)}
                  icon={<EditOutlined />}
                >
                  Chỉnh sửa
                </Button>
                <Button
                  style={{ marginLeft: "15px", background: "red" }}
                  className="heading-button-addnew btn"
                  onClick={() => deleteItemFunc(itemEdit?.id)}
                  icon={<DeleteOutlined />}
                  loading={loadingButtonDelete}
                >
                  Xóa
                </Button>
              </div>
            </Col>
            ) : type === "edit" ? (
            <Col md={12} span={24}>
              <div className="wrap-phong-ban">
                <Form
                  form={form}
                  name="phongban-control"
                  className="form-container wrapper-content-inner"
                  layout="vertical"
                >
                  <Row gutter={24}>
                    <Col md={12} xs={24}>
                      <FormItem
                        layout="vertical"
                        label="Tên đơn vị/ phòng ban"
                        name={["phongBan", "tenDonVi"]}
                        rules={[
                          {
                            type: "string",
                            required: true,
                            message: messages.ERROR_REQUIRED
                          }
                        ]}
                        initialValue={tenDonVi}
                      >
                        <Input
                          name="tenDonVi"
                          className="input-item"
                          placeholder="Nhập tên phòng ban"
                        />
                      </FormItem>
                    </Col>
                    <Col md={12} xs={24}>
                      <FormItem
                        label="Loại đơn vị"
                        name={["phongBan", "cap"]}
                        initialValue={cap}
                      >
                        <Select
                          // name="cap"
                          onChange={onChangeCap}
                          placeholder="Chọn cấp"
                          style={{ width: "100%" }}
                          options={listCap ? listCap : []}
                          fieldNames={{ label: "name", value: "cap" }}
                        ></Select>
                      </FormItem>
                    </Col>
                    <Col md={12} xs={24}>
                      <FormItem
                        layout="vertical"
                        name={["phongBan", "parentId"]}
                        label="Thuộc đơn vị"
                        rules={[
                          {
                            required: true,
                            message: messages.ERROR_REQUIRED
                          }
                        ]}
                        initialValue={parentId}
                      >
                        <TreeSelect
                          allowClear
                          showSearch
                          // name="parentId"
                          treeDefaultExpandAll
                          placeholder="Vui lòng chọn"
                          onChange={(e) => setParentId(e)}
                          treeData={treeDataSelect}
                          treeNodeFilterProp="title"
                          style={{ width: "100%" }}
                          dropdownStyle={{ overflow: "auto" }}
                        />
                      </FormItem>
                    </Col>
                    <Col md={12} xs={24}>
                      <FormItem
                        label="Thứ tự :"
                        name={["phongBan", "thuTu"]}
                        initialValue={thuTu}
                      >
                        <Input
                          name="thuTu"
                          placeholder="Thứ tự"
                          type="number"
                        />
                      </FormItem>
                    </Col>
                    <Col md={12} xs={24}>
                      <FormItem
                        label="Mã định danh:"
                        name={["phongBan", "maDinhDanh"]}
                        initialValue={maDinhDanh}
                      >
                        <Input
                          name="maDinhDanh"
                          placeholder="mã định danh"
                          className="input-item"
                        />
                      </FormItem>
                    </Col>
                    <Col md={12} xs={24}>
                      <Col md={12} xs={24}>
                        <FormItem
                          label="Đơn vị"
                          name={["phongBan", "isDonVi"]}
                          valuePropName="checked"
                          initialValue={isDonVi}
                        >
                          <Switch />
                        </FormItem>
                      </Col>
                    </Col>
                    <Col className="footer-form" span={24}>
                      <Button
                        onClick={save}
                        loading={loadingButton}
                        className="btn-save"
                        type="primary"
                      >
                        <SaveOutlined />
                        Lưu
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>) : null}
          </Row>
        </Skeleton>
      </Card>
    </Layout>
  );
};

export default PhongBan;
